import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChatTextarea, type ChatTextareaProps } from "./ChatTextarea";

export interface MentionTextareaProps extends ChatTextareaProps {
  /** usernames offered in the @-mention popover (e.g. presence + history + sitebot) */
  mentionCandidates: string[];
}

function mergeRefs<T>(
  a: React.Ref<T> | undefined,
  b: React.RefObject<T | null>,
) {
  return (node: T | null) => {
    (b as React.MutableRefObject<T | null>).current = node;
    if (typeof a === "function") a(node);
    else if (a && "current" in a) {
      (a as React.MutableRefObject<T | null>).current = node;
    }
  };
}

// the active @token immediately before the caret, if any. returns the '@' index + the query.
function activeMention(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const m = upto.match(/(?:^|\s)@([a-zA-Z0-9_-]*)$/);
  if (!m) return null;
  const query = m[1];
  return { start: caret - query.length - 1, query };
}

const MAX_SUGGESTIONS = 6;

/**
 * ChatTextarea + @-mention autocomplete. Type `@` to open a username popover; arrow keys /
 * Tab / Enter pick, Esc dismisses. Selecting inserts `@username `. Nav keys are intercepted in
 * the capture phase so they don't reach the textarea's own Enter-to-submit handler while the
 * popover is open. Purely a composing convenience — no notification side-effects.
 */
export const MentionTextarea = React.forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  function MentionTextarea(
    { value, onChange, onSubmit, mentionCandidates, ...rest },
    forwardedRef,
  ) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [tokenStart, setTokenStart] = useState<number | null>(null);
    const [active, setActive] = useState(0);

    const candidates = useMemo(() => {
      if (!open) return [];
      const q = query.toLowerCase();
      const seen = new Set<string>();
      const out: string[] = [];
      for (const u of mentionCandidates) {
        if (!u) continue;
        const key = u.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        if (key.startsWith(q)) out.push(u);
        if (out.length >= MAX_SUGGESTIONS) break;
      }
      return out;
    }, [open, query, mentionCandidates]);

    // close the popover once nothing matches
    useEffect(() => {
      if (open && candidates.length === 0) setOpen(false);
    }, [open, candidates.length]);

    const refreshMention = useCallback((text: string) => {
      const ta = innerRef.current;
      const caret = ta?.selectionStart ?? text.length;
      const m = activeMention(text, caret);
      if (m) {
        setTokenStart(m.start);
        setQuery(m.query);
        setActive(0);
        setOpen(true);
      } else {
        setOpen(false);
        setTokenStart(null);
      }
    }, []);

    const handleChange = useCallback(
      (v: string) => {
        onChange(v);
        refreshMention(v);
      },
      [onChange, refreshMention],
    );

    const insert = useCallback(
      (username: string) => {
        const ta = innerRef.current;
        if (!ta || tokenStart == null) return;
        const caret = ta.selectionStart ?? value.length;
        const before = value.slice(0, tokenStart);
        const after = value.slice(caret);
        const inserted = `@${username} `;
        const next = `${before}${inserted}${after}`;
        onChange(next);
        setOpen(false);
        setTokenStart(null);
        const pos = before.length + inserted.length;
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(pos, pos);
        });
      },
      [value, tokenStart, onChange],
    );

    // intercept nav keys before the textarea's own Enter-to-submit handler fires
    const onKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!open || candidates.length === 0) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setActive((i) => (i + 1) % candidates.length);
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setActive((i) => (i - 1 + candidates.length) % candidates.length);
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          insert(candidates[active] ?? candidates[0]);
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
          break;
        default:
          break;
      }
    };

    return (
      <div className="relative flex-1 min-w-0" onKeyDownCapture={onKeyDownCapture}>
        <ChatTextarea
          ref={mergeRefs(forwardedRef, innerRef)}
          value={value}
          onChange={handleChange}
          onSubmit={onSubmit}
          {...rest}
        />
        {open && candidates.length > 0 && (
          <div
            className="absolute bottom-full left-0 z-30 mb-1 max-h-40 w-48 overflow-y-auto rounded-sm border-2 border-ink bg-white"
            style={{ boxShadow: "2px 2px 0px rgba(0,0,0,0.15)" }}
          >
            {candidates.map((u, i) => (
              <button
                key={u}
                type="button"
                className={`block w-full px-2 py-1 text-left text-xs ${
                  i === active ? "bg-accent/30 font-bold" : "hover:bg-gray-100"
                }`}
                // mousedown (not click) so the textarea doesn't blur before we insert
                onMouseDown={(e) => {
                  e.preventDefault();
                  insert(u);
                }}
              >
                @{u}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

MentionTextarea.displayName = "MentionTextarea";
